/* eslint-disable no-plusplus */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import Loading from '../../components/Loading';
import ProductNameSearch from '../../components/ProductNameSearch';
import PaginationBar from '../../components/PaginationBar/PaginationBar';
import SectionMeta, { SectionHeaderRow } from '../../components/SectionMeta';
import axios from '../../services/axios';

import { Container } from '../../styles/layout/Container';

import {
   PageHeader,
   PageTitle,
   PageSubtitle,
   PageHeaderActions,
   PageHeaderText,
} from '../../styles/layout/PageHeader';
import {
   Section,
   SectionHeader,
   SectionTitle,
   SectionBody,
} from '../../styles/layout/Section';
import { FormGrid } from '../../styles/layout/FormGrid';
import { Field } from '../../styles/forms/Field';
import { ActionBar } from '../../styles/layout/ActionBar';
import { TableWrapper } from '../../styles/table/TableWrapper';
import { Table } from '../../styles/table/Table';
import { EmptyState } from '../../styles/feedback/EmptyState';

export default function Products() {
   const navigate = useNavigate();

   const [categories, setCategories] = useState([]);
   const [brands, setBrands] = useState([]);

   const [prodId, setProdId] = useState('');
   const [prodSku, setProdSku] = useState('');
   const [prodName, setProdName] = useState('');
   const [prodCategory, setProdCategory] = useState('');
   const [prodBrand, setProdBrand] = useState('');
   const [minStock, setMinStock] = useState('');

   const [products, setProducts] = useState([]);
   const [isLoading, setIsLoading] = useState(false);

   const [currentPage, setCurrentPage] = useState(1);
   const itemsPerPage = 15;

   // ✅ Estado para controlar a ordenação
   const [sortConfig, setSortConfig] = useState({
      key: null,
      direction: null, // null = padrão, 'asc' = ascendente, 'desc' = descendente
   });

   const nameSearchRef = useRef(null);

   useEffect(() => {
      async function bootstrap() {
         try {
            setIsLoading(true);

            const [categoriesRes, brandsRes] = await Promise.all([
               axios.get('/prodcategories'),
               axios.get('/prodbrands'),
            ]);

            setCategories(categoriesRes.data || []);
            setBrands(brandsRes.data || []);
         } catch (err) {
            const errorMsg =
               err?.response?.data?.errors?.[0] ||
               err?.message ||
               'Erro ao carregar categorias/marcas.';

            toast.error(errorMsg);

            if (err?.response?.status === 401) {
               toast.warn('401 - Não autorizado, refaça seu login.');
            }
         } finally {
            setIsLoading(false);
         }
      }

      bootstrap();
   }, []);

   const handleRowDoubleClick = (id) => {
      navigate(`/products/${id}`);
   };

   const handleClearFilters = () => {
      setProdId('');
      setProdSku('');
      setProdName('');
      setProdCategory('');
      setProdBrand('');
      setMinStock('');
      setProducts([]);
      setCurrentPage(1);
      // ✅ Limpa a ordenação também
      setSortConfig({ key: null, direction: null });
   };

   const handleGetProducts = async (e) => {
      e.preventDefault();

      nameSearchRef.current?.lock?.();

      setIsLoading(true);

      // ✅ Remove campos vazios, null e undefined antes de enviar
      const rawQuery = {
         id: prodId,
         prod_sku: prodSku,
         prod_name: prodName,
         category_id: Number(prodCategory) || undefined,
         brand_id: Number(prodBrand) || undefined,
         below_min_stock: minStock,
      };

      const query = Object.fromEntries(
         Object.entries(rawQuery).filter(
            // eslint-disable-next-line no-unused-vars
            ([_, v]) => v !== '' && v !== null && v !== undefined
         )
      );

      try {
         const response = await axios.post('/product-search', query);
         const { products: productList } = response.data.data;
         setProducts(productList || []);
         setCurrentPage(1);
         setSortConfig({ key: null, direction: null });
      } catch (err) {
         const errorMsg =
            err?.response?.data?.errors?.[0]?.message || // ✅ Novo formato de erro
            err?.response?.data?.errors?.[0] ||
            err?.message ||
            'Erro ao buscar produtos.';
         toast.error(errorMsg);

         if (err?.response?.status === 401) {
            toast.warn('401 - Não autorizado, refaça seu login.');
         }
      } finally {
         setIsLoading(false);
      }
   };

   // ✅ Função para lidar com o clique no cabeçalho
   const handleSort = (key) => {
      setSortConfig((prevConfig) => {
         if (prevConfig.key !== key) {
            // Nova coluna: começa com ASC
            return { key, direction: 'asc' };
         }

         // Mesma coluna: cicla entre ASC -> DESC -> PADRÃO
         if (prevConfig.direction === 'asc') {
            return { key, direction: 'desc' };
         }
         if (prevConfig.direction === 'desc') {
            return { key: null, direction: null }; // Volta ao padrão
         }
         return { key, direction: 'asc' };
      });

      // ✅ Volta para a primeira página ao ordenar
      setCurrentPage(1);
   };

   // ✅ Função para obter o ícone de ordenação
   const getSortIcon = (columnKey) => {
      if (sortConfig.key !== columnKey) {
         return ' ⇅'; // Ícone neutro
      }

      if (sortConfig.direction === 'asc') {
         return ' ▲'; // Ascendente
      }
      if (sortConfig.direction === 'desc') {
         return ' ▼'; // Descendente
      }
      return ' ⇅';
   };

   // ✅ Produtos ordenados (aplica ordenação antes da paginação)
   const sortedProducts = useMemo(() => {
      if (!sortConfig.key || !sortConfig.direction) {
         return products; // Retorna ordem original do backend
      }

      const sorted = [...products].sort((a, b) => {
         const aValue = a[sortConfig.key];
         const bValue = b[sortConfig.key];

         // Tratamento para valores nulos/undefined
         if (aValue === null || aValue === undefined) return 1;
         if (bValue === null || bValue === undefined) return -1;

         // Comparação numérica
         if (typeof aValue === 'number' && typeof bValue === 'number') {
            return sortConfig.direction === 'asc'
               ? aValue - bValue
               : bValue - aValue;
         }

         // Comparação de strings (case-insensitive)
         const aString = String(aValue).toLowerCase();
         const bString = String(bValue).toLowerCase();

         if (aString < bString) {
            return sortConfig.direction === 'asc' ? -1 : 1;
         }
         if (aString > bString) {
            return sortConfig.direction === 'asc' ? 1 : -1;
         }
         return 0;
      });

      return sorted;
   }, [products, sortConfig]);

   const totalPages = useMemo(() => {
      const raw = Math.ceil(sortedProducts.length / itemsPerPage);
      return raw; // pode ser 0 quando não há produtos (vamos tratar na UI)
   }, [sortedProducts.length]);

   const currentProducts = useMemo(() => {
      const indexOfLast = currentPage * itemsPerPage;
      const indexOfFirst = indexOfLast - itemsPerPage;
      return sortedProducts.slice(indexOfFirst, indexOfLast);
   }, [sortedProducts, currentPage]);

   const handlePageChange = (pageNumber) => {
      if (!totalPages) return; // sem resultados
      const next = Math.min(Math.max(1, pageNumber), totalPages);
      setCurrentPage(next);
   };

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Produtos</PageTitle>
               <PageSubtitle>
                  Pesquise e visualize resultados. Dê duplo clique na linha para
                  abrir o produto.
               </PageSubtitle>
            </PageHeaderText>

            <PageHeaderActions>
               <button
                  type="button"
                  onClick={handleClearFilters}
                  disabled={isLoading}
                  title="Limpar filtros e resultados"
               >
                  Limpar
               </button>
            </PageHeaderActions>
         </PageHeader>

         {/* Filtros */}
         <Section>
            <SectionHeader>
               <SectionTitle>Pesquisa</SectionTitle>
            </SectionHeader>

            <SectionBody>
               <form onSubmit={handleGetProducts}>
                  <FormGrid $cols={12} $colsMobile={12}>
                     <Field $col={2} $colMobile={6}>
                        <label>ID do produto</label>
                        <input
                           type="text"
                           value={prodId}
                           onChange={(e) => setProdId(e.target.value)}
                           placeholder="Ex.: 17"
                        />
                     </Field>

                     <Field $col={10} $colMobile={12}>
                        <label>Nome do produto</label>

                        {/* ✅ novo componente global */}
                        <ProductNameSearch
                           ref={nameSearchRef}
                           value={prodName}
                           onChange={(v) => setProdName(v)}
                           onSelect={(p) => {
                              // como você pediu: só preencher o nome corretamente
                              if (p?.prod_name) setProdName(p.prod_name);
                           }}
                           placeholder="Digite 3+ letras para sugerir..."
                           disabled={isLoading}
                           nameLike
                           showHint
                        />
                     </Field>

                     <Field $col={4} $colMobile={12}>
                        <label>Categoria</label>
                        <select
                           value={prodCategory}
                           onChange={(e) => setProdCategory(e.target.value)}
                        >
                           <option value="">Selecione uma categoria</option>
                           {categories.map((category) => (
                              <option
                                 key={Number(category.id)}
                                 value={category.id}
                              >
                                 {category.name}
                              </option>
                           ))}
                        </select>
                     </Field>

                     <Field $col={4} $colMobile={12}>
                        <label>Marca</label>
                        <select
                           value={prodBrand}
                           onChange={(e) => setProdBrand(e.target.value)}
                        >
                           <option value="">Selecione a marca</option>
                           {brands.map((brand) => (
                              <option key={String(brand.id)} value={brand.id}>
                                 {brand.name}
                              </option>
                           ))}
                        </select>
                     </Field>

                     <Field $col={4} $colMobile={12}>
                        <label>Estoque</label>
                        <select
                           value={minStock}
                           onChange={(e) => setMinStock(e.target.value)}
                        >
                           <option value="">Sem filtro de estoque</option>
                           <option value="true">
                              Abaixo do estoque mínimo
                           </option>
                        </select>
                     </Field>

                     <Field $col={12} $colMobile={12} $alignEnd>
                        <label>&nbsp;</label>
                        <ActionBar>
                           <button type="submit" disabled={isLoading}>
                              Pesquisar
                           </button>
                        </ActionBar>
                     </Field>
                  </FormGrid>
               </form>
            </SectionBody>
         </Section>

         {/* Resultados */}
         <Section>
            <SectionHeader>
               <SectionHeaderRow>
                  <SectionTitle>Resultados</SectionTitle>
                  <SectionMeta
                     items={[
                        {
                           key: 'count',
                           label: 'Total de produtos:',
                           value: products.length,
                        },
                        {
                           key: 'page',
                           label: 'Página',
                           value: `${currentPage}/${totalPages || 1}`,
                        },
                     ]}
                  />
               </SectionHeaderRow>
            </SectionHeader>

            <SectionBody>
               {products.length === 0 ? (
                  <EmptyState>
                     <h4>Sem resultados</h4>
                     <p>
                        Use os filtros acima e clique em &quot;Pesquisar&quot;.
                     </p>
                  </EmptyState>
               ) : (
                  <>
                     <TableWrapper>
                        <Table>
                           <thead>
                              <tr>
                                 <th
                                    onClick={() => handleSort('id')}
                                    style={{
                                       cursor: 'pointer',
                                       userSelect: 'none',
                                    }}
                                    title="Clique para ordenar"
                                 >
                                    ID{getSortIcon('id')}
                                 </th>
                                 <th
                                    onClick={() => handleSort('prod_sku')}
                                    style={{
                                       cursor: 'pointer',
                                       userSelect: 'none',
                                    }}
                                    title="Clique para ordenar"
                                 >
                                    SKU{getSortIcon('prod_sku')}
                                 </th>
                                 <th
                                    onClick={() => handleSort('prod_name')}
                                    style={{
                                       cursor: 'pointer',
                                       userSelect: 'none',
                                    }}
                                    title="Clique para ordenar"
                                 >
                                    Nome produto{getSortIcon('prod_name')}
                                 </th>
                                 <th
                                    onClick={() => handleSort('category_name')}
                                    style={{
                                       cursor: 'pointer',
                                       userSelect: 'none',
                                    }}
                                    title="Clique para ordenar"
                                 >
                                    Categoria{getSortIcon('category_name')}
                                 </th>
                                 <th
                                    onClick={() => handleSort('brand_name')}
                                    style={{
                                       cursor: 'pointer',
                                       userSelect: 'none',
                                    }}
                                    title="Clique para ordenar"
                                 >
                                    Marca{getSortIcon('brand_name')}
                                 </th>
                                 <th
                                    onClick={() =>
                                       handleSort('total_stock_qtd')
                                    }
                                    style={{
                                       textAlign: 'right',
                                       cursor: 'pointer',
                                       userSelect: 'none',
                                    }}
                                    title="Clique para ordenar"
                                 >
                                    Qtd.{getSortIcon('total_stock_qtd')}
                                 </th>
                                 <th
                                    onClick={() => handleSort('min_stock_qtd')}
                                    style={{
                                       textAlign: 'right',
                                       cursor: 'pointer',
                                       userSelect: 'none',
                                    }}
                                    title="Clique para ordenar"
                                 >
                                    Estoque mín.{getSortIcon('min_stock_qtd')}
                                 </th>
                              </tr>
                           </thead>

                           <tbody>
                              {currentProducts.map((product) => (
                                 <tr
                                    key={String(product.id)}
                                    onDoubleClick={() =>
                                       handleRowDoubleClick(product.id)
                                    }
                                    style={{ cursor: 'pointer' }}
                                    title="Duplo clique para abrir"
                                 >
                                    <td>{product.id}</td>
                                    <td>{product.sku}</td>
                                    <td title={product.name}>{product.name}</td>
                                    <td>
                                       {product.category
                                          ? product.category
                                          : '-'}
                                    </td>
                                    <td>
                                       {product.brand ? product.brand : '-'}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                       {product.total_stock === null
                                          ? '0'
                                          : product.total_stock}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                       {product.min_stock === null
                                          ? '0'
                                          : product.min_stock}
                                    </td>
                                 </tr>
                              ))}
                           </tbody>
                        </Table>
                     </TableWrapper>
                     <div style={{ padding: '16, 0' }}>&nbsp;</div>

                     <PaginationBar
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={products.length}
                        itemLabel="produtos"
                        onPageChange={handlePageChange}
                        disabled={isLoading}
                     />
                  </>
               )}
            </SectionBody>
         </Section>
      </Container>
   );
}
